/**
 * PerformanceSidebar — T2 sidebar for the Performance (KPI) module.
 *
 * Sections: My KPIs | Team | Organization (executive) | KPI Catalog | Cycles (admin)
 */
import React from 'react';
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3';
import Users from 'lucide-react/dist/esm/icons/users';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import BookOpen from 'lucide-react/dist/esm/icons/book-open';
import CalendarRange from 'lucide-react/dist/esm/icons/calendar-range';
import type { LucideIcon } from 'lucide-react';
import { useUIStore } from '../../../stores/uiStore';
import { useOrgStore } from '../../../stores/orgStore';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permissions } from '../../../config/permissions';
import { getTranslation } from '../../../i18n';

interface SectionItem {
  id: string;
  labelKey: string;
  descriptionKey: string;
  icon: LucideIcon;
  permission?: string;
  adminOnly?: boolean;
}

const SECTIONS: SectionItem[] = [
  {
    id: 'my-kpis',
    labelKey: 'perfNavMyKpis',
    descriptionKey: 'perfNavMyKpisDesc',
    icon: BarChart3,
  },
  {
    id: 'team',
    labelKey: 'perfNavTeam',
    descriptionKey: 'perfNavTeamDesc',
    icon: Users,
  },
  {
    id: 'organization',
    labelKey: 'perfNavOrganization',
    descriptionKey: 'perfNavOrganizationDesc',
    icon: Building2,
    permission: Permissions.ORG_KPI_VIEW_ALL,
  },
  {
    id: 'catalog',
    labelKey: 'perfNavKpiCatalog',
    descriptionKey: 'perfNavKpiCatalogDesc',
    icon: BookOpen,
  },
  {
    id: 'cycles',
    labelKey: 'perfNavCycles',
    descriptionKey: 'perfNavCyclesDesc',
    icon: CalendarRange,
    adminOnly: true,
  },
];

const PerformanceSidebar: React.FC = () => {
  const { performanceSection, setPerformanceSection, language } = useUIStore();
  const { userContext } = useOrgStore();
  const { hasPermission } = usePermissions();
  const t = (key: string) => getTranslation(language, key);

  const isAdmin = userContext?.system_role !== 'user';

  const visibleSections = SECTIONS.filter((s) => {
    if (s.adminOnly && !isAdmin) return false;
    if (s.permission && !hasPermission(s.permission as any)) return false;
    return true;
  });

  return (
    <aside className="fixed top-[60px] left-[60px] w-[240px] h-[calc(100vh-60px)] bg-surface-page border-r border-subtle z-sticky flex flex-col transition-all duration-300">
      <div className="px-4 pt-4 pb-3 border-b border-subtle">
        <h2 className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">
          {t('perfSidebarTitle')}
        </h2>
      </div>

      <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
        {visibleSections.map((section) => {
          const isActive = performanceSection === section.id;
          const Icon = section.icon;

          return (
            <button
              key={section.id}
              onClick={() => setPerformanceSection(section.id)}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left
                transition-all duration-150 border-none outline-none cursor-pointer
                ${isActive
                  ? 'bg-status-success/10 text-status-success'
                  : 'text-content-tertiary hover:text-content-secondary hover:bg-surface-primary'
                }
              `}
            >
              <div
                className={`
                  w-8 h-8 rounded-lg flex items-center justify-center shrink-0
                  ${isActive ? 'bg-status-success/15' : 'bg-surface-primary'}
                `}
              >
                <Icon
                  size={16}
                  strokeWidth={isActive ? 2.2 : 1.8}
                  className={isActive ? 'text-status-success' : 'text-content-muted'}
                />
              </div>
              <div className="min-w-0">
                <span className={`block text-sm font-medium ${isActive ? 'text-status-success' : 'text-content-secondary'}`}>
                  {t(section.labelKey)}
                </span>
                <span className="block text-[11px] text-content-muted mt-0.5 truncate">
                  {t(section.descriptionKey)}
                </span>
              </div>
              {isActive && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-status-success shrink-0" />
              )}
            </button>
          );
        })}
      </nav>
    </aside>
  );
};

export default React.memo(PerformanceSidebar);
